import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { buttonClasses } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

/** Official Google "G" mark (brand guidelines require the four-color logo). */
function GoogleLogo() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * Public sign-in screen. Sign-in is always an explicit user action -- nothing
 * redirects to the IdP automatically. The Hosted UI then forwards straight to
 * Google (identity_provider hint + Google-only pool client).
 */
export function LoginPage() {
  const { isLoading, isAuthenticated, error, signIn } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Session restore in flight: avoid flashing the button.
  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-brand-50/60">
        <Spinner label="Loading" />
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-brand-50/60 px-4">
      <div className="w-full max-w-sm">
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600"
            >
              <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l2.2 6.2L20 10l-5.8 1.8L12 18l-2.2-6.2L4 10l5.8-1.8L12 2z" />
              </svg>
            </span>
            <span className="text-lg font-semibold tracking-tight text-gray-900">saasd</span>
          </div>

          <h1 className="mt-6 text-xl font-semibold text-gray-900">Sign in to your workspace</h1>
          <p className="mt-1.5 text-sm leading-6 text-gray-600">
            Configure, run, and chat with your AI agents.
          </p>

          {error && (
            <p
              role="alert"
              className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error.message}
            </p>
          )}

          <button
            type="button"
            disabled={redirecting}
            onClick={() => {
              setRedirecting(true);
              void signIn();
            }}
            className={buttonClasses({
              variant: 'secondary',
              className: 'mt-6 w-full py-2.5 text-sm',
            })}
          >
            <GoogleLogo />
            {redirecting ? 'Redirecting to Google...' : 'Continue with Google'}
          </button>
        </div>

        <p className="mt-6 text-center text-xs leading-5 text-gray-500">
          Access is restricted to Google sign-in.
        </p>
      </div>
    </main>
  );
}
